import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import usePrevious from 'lib/src/hooks/usePrevious';
import useForm from '@components/form/hooks/useForm';

import { updateSuperAdminUserPassword } from '@actions/superAdminUsers';
import {
    getSuperAdminUserIsPosting,
    getSuperAdminUserPostSuccess,
} from '@selectors/superAdminUsers';
import { useHistory } from 'react-router-dom';

export default function useUpdateSuperAdminPassword(
    userID: number,
): UpdateSuperAdminPasswordReturn {
    const dispatch = useDispatch();
    const history = useHistory();

    const closeModal = useCallback(() => {
        history.replace(`/superadmins/${userID}`);
    }, [history, userID]);

    const [formState, handleChange] = useForm({ password: '', confirmPassword: '' });

    const handleSubmit = useCallback(() => {
        dispatch(updateSuperAdminUserPassword(userID, formState));
    }, [dispatch, userID, formState]);

    const postSuccess = useSelector(getSuperAdminUserPostSuccess);
    const isPosting = useSelector(getSuperAdminUserIsPosting);
    const prevPostSuccess = usePrevious(postSuccess);

    useEffect(() => {
        if (!prevPostSuccess && postSuccess) {
            closeModal();
        }
    }, [postSuccess, prevPostSuccess, closeModal]);

    return {
        closeModal,
        handleSubmit,
        formState,
        handleChange,
        isPosting,
    };
}

interface FormState {
    password: string;
    confirmPassword: string;
}

interface UpdateSuperAdminPasswordReturn {
    closeModal: () => void;
    handleSubmit: () => void;
    formState: FormState;
    handleChange: <T>(name: 'password' | 'confirmPassword', value: T) => void;
    isPosting: boolean;
}
